import { Star } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { NAV, visibleItems } from '@/components/layout/nav'
import { confirmLeave } from '@/lib/leaveGuard'
import type { Me } from '@/lib/auth'
import type { OpMeta } from '@/catalog/types'
import { api } from '@/lib/api'
import { fetchOps } from '@/lib/ops'
import { toggleFavorite, useOpPrefs } from '@/lib/opPrefs'
import { matchesAllWords, searchOps } from '@/lib/opSearch'

// ---- types ----

type PaletteEntry = {
  id: string
  group: 'favorites' | 'recent' | 'pages' | 'operations' | 'users' | 'projects'
  label: string
  /** operation entries carry their id so the row can offer a favorite toggle */
  opId?: string
  hint?: string
  to: string
}

// With a query, ⌘K lists the best operation matches only.
const OP_RESULTS_MAX = 50
// …and up to this many projects from the cache.
const PROJECT_RESULTS_MAX = 8

type ProjectHit = { key: string; name: string }

/** GET /api/projects answers a list; anything else (an error body, {}) counts as no hits. */
const asProjects = (v: unknown): ProjectHit[] =>
  Array.isArray(v)
    ? v.filter((p): p is ProjectHit => !!p && typeof p.key === 'string' && typeof p.name === 'string')
    : []

/**
 * The backend answers projects by name; someone typing a key wants that project first:
 * exact key, then key prefix, then the backend's order (stable sort).
 */
function rankProjects(hits: ProjectHit[], q: string): ProjectHit[] {
  const k = q.trim().toUpperCase()
  const rank = (p: ProjectHit) => {
    const key = p.key.toUpperCase()
    return key === k ? 0 : key.startsWith(k) ? 1 : 2
  }
  return [...hits].sort((a, b) => rank(a) - rank(b))
}

// ---- helpers ----

/** Same pages as the side menu, in menu order. */
const navPages = (me: Me) => NAV.flatMap((g) => visibleItems(g.items, me))

const GROUP_LABELS: Record<PaletteEntry['group'], string> = {
  favorites: 'favorites',
  recent: 'recent',
  pages: 'pages',
  operations: 'operations',
  users: 'users',
  projects: 'projects',
}

// ---- component ----

export function CommandPalette({ me, open, onClose }: { me: Me; open: boolean; onClose: () => void }) {
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const [ops, setOps] = useState<OpMeta[] | null>(null)
  const { recent, favorites } = useOpPrefs()
  const [userResults, setUserResults] = useState<{ account_id: string; display_name: string; email: string | null }[]>([])
  const [selectedIndex, setSelectedIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)
  const [projectResults, setProjectResults] = useState<ProjectHit[]>([])
  const userDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const projectDebounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const loadedRef = useRef(false)

  // Load ops once when palette first opens
  useEffect(() => {
    if (!open) return
    if (loadedRef.current) return
    loadedRef.current = true

    fetchOps().then(setOps).catch(() => setOps([]))
  }, [open])

  // Focus input when opening
  useEffect(() => {
    if (open) {
      setQuery('')
      setSelectedIndex(0)
      setUserResults([])
      setProjectResults([])
      setTimeout(() => inputRef.current?.focus(), 10)
    }
  }, [open])

  // User search (debounced)
  useEffect(() => {
    if (userDebounce.current) clearTimeout(userDebounce.current)
    if (!query.trim()) {
      setUserResults([])
      return
    }
    userDebounce.current = setTimeout(() => {
      api<{ account_id: string; display_name: string; email: string | null }[]>(
        `/users?q=${encodeURIComponent(query.trim())}`,
      )
        .then((v) => setUserResults(Array.isArray(v) ? v : []))
        .catch(() => setUserResults([]))
    }, 200)
    return () => {
      if (userDebounce.current) clearTimeout(userDebounce.current)
    }
  }, [query])

  // Project search (debounced), from the Jira cache
  useEffect(() => {
    if (projectDebounce.current) clearTimeout(projectDebounce.current)
    const q = query.trim()
    if (!q) {
      setProjectResults([])
      return
    }
    let alive = true
    projectDebounce.current = setTimeout(() => {
      api<unknown>(`/projects?q=${encodeURIComponent(q)}`)
        .then((v) => { if (alive) setProjectResults(rankProjects(asProjects(v), q).slice(0, PROJECT_RESULTS_MAX)) })
        .catch(() => { if (alive) setProjectResults([]) })
    }, 200)
    return () => {
      alive = false
      if (projectDebounce.current) clearTimeout(projectDebounce.current)
    }
  }, [query])

  // Build entries
  const entries: PaletteEntry[] = useCallback((): PaletteEntry[] => {
    const q = query.trim()
    const result: PaletteEntry[] = []

    const summaryOf = (id: string) => ops?.find((o) => o.operation_id === id)?.summary
    // once the catalog is loaded, drop stored ids of removed/renamed operations
    const known = (id: string) => !ops || ops.some((o) => o.operation_id === id)
    if (!q) {
      // No query: the admin's own Favorites, then what they ran recently (this browser)
      for (const [group, ids] of [['favorites', favorites], ['recent', recent]] as const) {
        for (const id of ids.filter(known)) {
          result.push({ id: `${group}:${id}`, group, label: id, hint: summaryOf(id), to: `/operations/${id}`, opId: id })
        }
      }
    }

    // Pages
    for (const p of navPages(me)) {
      if (!matchesAllWords(p.label, q)) continue
      result.push({
        id: `page:${p.to}`,
        group: 'pages',
        label: p.label,
        to: p.to,
      })
    }

    // Operations
    if (ops) {
      for (const op of q ? searchOps(ops, q).slice(0, OP_RESULTS_MAX) : ops) {
        result.push({
          id: `op:${op.operation_id}`,
          group: 'operations',
          label: op.operation_id,
          hint: op.summary,
          to: `/operations/${op.operation_id}`,
          opId: op.operation_id,
        })
      }
    }

    // Users (from live search)
    for (const u of userResults) {
      result.push({
        id: `user:${u.account_id}`,
        group: 'users',
        label: u.display_name,
        hint: u.email ?? u.account_id,
        to: `/users/${u.account_id}`,
      })
    }

    // Projects (from the cache)
    for (const p of projectResults) {
      result.push({
        id: `project:${p.key}`,
        group: 'projects',
        label: p.key,
        hint: p.name,
        to: `/projects/${encodeURIComponent(p.key)}`,
      })
    }

    return result
  }, [query, ops, recent, favorites, userResults, projectResults, me])()

  // Reset selection when entries change
  useEffect(() => {
    setSelectedIndex(0)
  }, [entries.length, query])

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.querySelector('[data-selected="true"]')
    el?.scrollIntoView({ block: 'nearest' })
  }, [selectedIndex])

  function activate(entry: PaletteEntry) {
    // a page with unsaved changes may veto the navigation — the palette stays open then
    if (!confirmLeave()) return
    onClose()
    navigate(entry.to)
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setSelectedIndex((i) => Math.min(i + 1, entries.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setSelectedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (entries[selectedIndex]) activate(entries[selectedIndex])
    } else if ((e.key === 'd' || e.key === 'D') && (e.metaKey || e.ctrlKey)) {
      // ⌘D / Ctrl+D: toggle favorite on the selected operation row
      const opId = entries[selectedIndex]?.opId
      if (opId) {
        e.preventDefault()
        toggleFavorite(opId)
      }
    } else if (e.key === 'Escape') {
      onClose()
    }
  }

  // Group entries for rendering
  const grouped: { group: PaletteEntry['group']; items: PaletteEntry[] }[] = []
  let lastGroup: PaletteEntry['group'] | null = null
  for (const entry of entries) {
    if (entry.group !== lastGroup) {
      grouped.push({ group: entry.group, items: [entry] })
      lastGroup = entry.group
    } else {
      grouped[grouped.length - 1].items.push(entry)
    }
  }

  if (!open) return null

  return (
    // Backdrop
    <div
      className="fixed inset-0 z-50 bg-background/70"
      onClick={onClose}
      aria-modal="true"
      role="dialog"
      aria-label="Command palette"
    >
      {/* Panel */}
      <div
        className="pop-in mx-auto mt-[12vh] flex max-h-[60vh] w-full max-w-xl flex-col overflow-hidden rounded-lg border border-line-strong bg-popover"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
      >
        {/* Input */}
        <div className="flex items-center gap-2.5 border-b border-line px-4 py-3">
          <span className="font-mono text-[11px] text-text-3" aria-hidden>
            ⌘K
          </span>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Go to page, run operation, find user or project…"
            className="min-w-0 flex-1 bg-transparent text-[13px] text-foreground placeholder:text-text-3 outline-none"
            autoComplete="off"
            spellCheck={false}
            aria-label="Command palette search"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="text-[12px] text-text-3 transition-colors hover:text-foreground"
              tabIndex={-1}
            >
              ✕
            </button>
          )}
        </div>

        {/* Results */}
        <div ref={listRef} className="flex-1 overflow-y-auto" role="listbox">
          {entries.length === 0 && (
            <div className="px-4 py-6 text-center text-[13px] text-text-3">
              {ops === null ? 'Loading…' : 'No results'}
            </div>
          )}
          {grouped.map(({ group, items }) => (
            <div key={group} role="group" aria-label={GROUP_LABELS[group]}>
              {/* Group header */}
              <div className="px-4 pb-1 pt-3 text-[11px] font-medium uppercase tracking-[0.08em] text-text-3" aria-hidden>
                {GROUP_LABELS[group]}
              </div>
              {items.map((entry) => {
                const globalIndex = entries.indexOf(entry)
                const isSelected = globalIndex === selectedIndex
                const fav = entry.opId ? favorites.includes(entry.opId) : false
                return (
                  <div key={entry.id} role="presentation" className="group/row relative">
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-selected={isSelected}
                    onClick={() => activate(entry)}
                    className={[
                      'flex w-full items-baseline gap-2 border-l-2 px-4 py-1.5 text-left transition-colors',
                      isSelected ? 'border-primary bg-surface-selected text-foreground' : 'border-transparent text-text-2',
                      entry.opId ? 'pr-10' : '',
                    ].join(' ')}
                  >
                    <span className={`min-w-0 flex-1 truncate text-[13px] ${entry.group === 'pages' || entry.group === 'users' ? '' : 'font-mono'}`}>
                      {entry.label}
                    </span>
                    {entry.hint && (
                      <span className="max-w-[40%] shrink-0 truncate text-[11px] text-text-3">
                        {entry.hint}
                      </span>
                    )}
                  </button>
                  {entry.opId && (
                    <button
                      type="button"
                      onClick={() => toggleFavorite(entry.opId!)}
                      tabIndex={-1}
                      aria-pressed={fav}
                      aria-label={fav ? `Remove ${entry.opId} from favorites` : `Add ${entry.opId} to favorites`}
                      title={fav ? 'Remove from favorites' : 'Add to favorites'}
                      className={[
                        'absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded transition-colors hover:text-foreground focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-ring',
                        fav ? 'text-primary' : 'text-text-3 opacity-0 group-hover/row:opacity-100',
                        isSelected && !fav ? 'opacity-100' : '',
                      ].join(' ')}
                    >
                      <Star className="h-3.5 w-3.5" fill={fav ? 'currentColor' : 'none'} aria-hidden />
                    </button>
                  )}
                  </div>
                )
              })}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 border-t border-line px-4 py-2 font-mono text-[11px] text-text-3">
          <span>↑↓ navigate</span>
          <span>↵ open</span>
          <span>⌘D favorite</span>
          <span>esc close</span>
        </div>
      </div>
    </div>
  )
}
